/**
 * Data hooks for the medication workstream. All persistence goes through
 * `repositories.medication` (contract §3); components never import Supabase.
 *
 * Failure model (contract §3): when the cloud is not configured the hook
 * reports `status: 'unavailable'` so the UI can show an honest
 * 'cloud niet ingesteld' state. Other failures report `status: 'error'`
 * with a retry path.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { repositories } from '@/services/repositories'
import { isAppError } from '@/lib/supabase/errors'
import { useAuth } from '@/features/auth'
import type {
  AppError,
  MedicationIntakeEvent,
  MedicationOccurrence,
  MedicationSchedule,
} from '@/types'

export type LoadStatus = 'loading' | 'ready' | 'unavailable' | 'error'

export type OccurrenceItem = {
  occurrence: MedicationOccurrence
  schedule: MedicationSchedule | null
  intakeEvent: MedicationIntakeEvent | null
}

export type MedicationDayState = {
  status: LoadStatus
  items: OccurrenceItem[]
  error: AppError | null
}

function toErrorState(error: unknown): { status: 'unavailable' | 'error'; error: AppError | null } {
  if (isAppError(error)) {
    if (error.code === 'SUPABASE_NOT_CONFIGURED') return { status: 'unavailable', error: null }
    return { status: 'error', error }
  }
  return {
    status: 'error',
    error: {
      code: 'UNKNOWN',
      userMessage: 'Er ging iets mis. Probeer het opnieuw.',
      retryable: true,
      source: 'database',
    },
  }
}

export function errorMessage(error: unknown): string {
  if (isAppError(error)) return error.userMessage
  return 'Er ging iets mis. Probeer het opnieuw.'
}

export function isNotConfigured(error: unknown): boolean {
  return isAppError(error) && error.code === 'SUPABASE_NOT_CONFIGURED'
}

/**
 * Joins occurrences for [date, date] with their schedule and latest intake
 * event. When `clientId` is omitted the repository scopes to the signed-in
 * client (session identity — never caller-supplied ids for own data).
 */
export function useMedicationDay(date: string, clientId?: string) {
  const { userId } = useAuth()
  const effectiveClientId = clientId ?? userId ?? undefined
  const [state, setState] = useState<MedicationDayState>({ status: 'loading', items: [], error: null })
  const [busyAction, setBusyAction] = useState(false)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const load = useCallback(async () => {
    if (!effectiveClientId) {
      setState({ status: 'unavailable', items: [], error: null })
      return
    }
    setState((prev) => ({ ...prev, status: 'loading', error: null }))
    try {
      const [schedules, occurrences, intakeEvents] = await Promise.all([
        repositories.medication.listSchedules(clientId),
        repositories.medication.listOccurrences(date, date, clientId),
        repositories.medication.listIntakeEvents(effectiveClientId, 200),
      ])
      const scheduleById = new Map(schedules.map((schedule) => [schedule.id, schedule]))
      // Latest intake event per occurrence wins.
      const intakeByOccurrence = new Map<string, MedicationIntakeEvent>()
      for (const event of intakeEvents) {
        const existing = intakeByOccurrence.get(event.occurrenceId)
        if (!existing || existing.updatedAt < event.updatedAt) {
          intakeByOccurrence.set(event.occurrenceId, event)
        }
      }
      const items: OccurrenceItem[] = occurrences
        .map((occurrence) => ({
          occurrence,
          schedule: scheduleById.get(occurrence.scheduleId) ?? null,
          intakeEvent: intakeByOccurrence.get(occurrence.id) ?? null,
        }))
        .sort((a, b) => a.occurrence.localTime.localeCompare(b.occurrence.localTime))
      if (mountedRef.current) setState({ status: 'ready', items, error: null })
    } catch (error) {
      const next = toErrorState(error)
      if (mountedRef.current) setState({ status: next.status, items: [], error: next.error })
    }
  }, [date, clientId, effectiveClientId])

  useEffect(() => {
    void load()
  }, [load])

  const markTaken = useCallback(
    async (occurrenceId: string, note?: string) => {
      setBusyAction(true)
      try {
        await repositories.medication.markTaken(occurrenceId, note?.trim() ? note.trim() : undefined)
        await load()
      } finally {
        if (mountedRef.current) setBusyAction(false)
      }
    },
    [load],
  )

  const undoTaken = useCallback(
    async (intakeEventId: string) => {
      setBusyAction(true)
      try {
        await repositories.medication.undoTaken(intakeEventId)
        await load()
      } finally {
        if (mountedRef.current) setBusyAction(false)
      }
    },
    [load],
  )

  return { ...state, busyAction, refresh: load, markTaken, undoTaken }
}

/** Bounded undo helpers live in ./undoPolicy (pure, unit tested). */
export { isUndoAllowed, undoMinutesRemaining } from './undoPolicy'

/** Intake history for the client agenda / caregiver intake page. */
export function useIntakeHistory(clientId: string | null, limit = 50) {
  const [state, setState] = useState<{ status: LoadStatus; events: MedicationIntakeEvent[]; error: AppError | null }>({
    status: 'loading',
    events: [],
    error: null,
  })

  const load = useCallback(async () => {
    if (!clientId) {
      setState({ status: 'unavailable', events: [], error: null })
      return
    }
    setState((prev) => ({ ...prev, status: 'loading', error: null }))
    try {
      const events = await repositories.medication.listIntakeEvents(clientId, limit)
      setState({ status: 'ready', events, error: null })
    } catch (error) {
      const next = toErrorState(error)
      setState({ status: next.status, events: [], error: next.error })
    }
  }, [clientId, limit])

  useEffect(() => {
    void load()
  }, [load])

  return useMemo(() => ({ ...state, refresh: load }), [state, load])
}

/** Schedules for a client (caregiver schedule management). */
export function useMedicationSchedules(clientId: string | null) {
  const [state, setState] = useState<{ status: LoadStatus; schedules: MedicationSchedule[]; error: AppError | null }>({
    status: 'loading',
    schedules: [],
    error: null,
  })

  const load = useCallback(async () => {
    if (!clientId) {
      setState({ status: 'unavailable', schedules: [], error: null })
      return
    }
    setState((prev) => ({ ...prev, status: 'loading', error: null }))
    try {
      const schedules = await repositories.medication.listSchedules(clientId)
      setState({ status: 'ready', schedules, error: null })
    } catch (error) {
      const next = toErrorState(error)
      setState({ status: next.status, schedules: [], error: next.error })
    }
  }, [clientId])

  useEffect(() => {
    void load()
  }, [load])

  return useMemo(() => ({ ...state, refresh: load }), [state, load])
}
